/**
 * SyncStatus - PWA connection & sync status indicator (WCAG Accessible)
 * 
 * STATE MODEL:
 * - syncState: 'online' | 'offline' | 'syncing' | 'reauth_required' | 'error'
 * - pendingCount: number of queued writes
 * - lastSyncAt: timestamp of last successful sync
 * - lastErrorCode: 401/403/500 etc
 * - lastErrorAt: timestamp of last error
 * 
 * TELEMETRY (P2.6):
 * - lastQueueFlushAt: timestamp of last queue flush attempt
 * - lastQueueFlushResult: 'success' | 'failed' | 'partial'
 * - lastQueueFlushErrorCode: HTTP status code if failed
 * - lastQueueFlushErrorMessage: short error description
 * - lastOnlineAt: when we last went online
 * - lastOfflineAt: when we last went offline
 * - failedPermissionCount: items failed due to 403
 * 
 * SEMANTICS (truthful):
 * - "Online" / "Offline" = browser connectivity state
 * - "Last updated" = timestamp of last successful API fetch
 * - "X pending" = queued writes awaiting sync
 * 
 * RE-AUTH FLOW:
 * When syncState === 'reauth_required':
 * - Show "Re-auth required to sync" message
 * - Show pendingCount and lastErrorAt
 * - Button: "Log in to sync" (navigates to login)
 * - Never drops queued writes
 * 
 * 403 HANDLING:
 * - Items with 403 are marked as failed-permission
 * - Do not retry endlessly
 * - Show "Insufficient permission. Contact admin."
 */
import React, { useState, useEffect, useCallback } from 'react';

const formatLastUpdate = (timestamp) => {
  if (!timestamp) return '—';
  
  const date = new Date(timestamp);
  const now = Date.now();
  const diff = now - timestamp;
  
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)} min ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)} hours ago`;
  
  return date.toLocaleString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Persist queue to localStorage (for SW to restore on reload)
const QUEUE_STORAGE_KEY = 'riskee_pending_writes';
// Flag to signal re-auth required state persists across page refreshes
const REAUTH_FLAG_KEY = 'riskee_reauth_required';
// Telemetry storage key
const TELEMETRY_KEY = 'riskee_sync_telemetry';
// Failed permission items (403)
const FAILED_PERMISSION_KEY = 'riskee_failed_permissions';

const loadPersistedQueue = () => {
  try {
    const stored = localStorage.getItem(QUEUE_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const persistQueue = (queue) => {
  try {
    localStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(queue));
  } catch (e) {
    console.error('Failed to persist queue:', e);
  }
};

const loadReauthFlag = () => {
  try {
    const data = localStorage.getItem(REAUTH_FLAG_KEY);
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
};

const setReauthFlag = (errorCode, errorAt) => {
  localStorage.setItem(REAUTH_FLAG_KEY, JSON.stringify({ errorCode, errorAt }));
};

const clearReauthFlag = () => {
  localStorage.removeItem(REAUTH_FLAG_KEY);
};

// Telemetry helpers
const loadTelemetry = () => {
  try {
    const data = localStorage.getItem(TELEMETRY_KEY);
    return data ? JSON.parse(data) : {};
  } catch {
    return {};
  }
};

const saveTelemetry = (telemetry) => {
  try {
    localStorage.setItem(TELEMETRY_KEY, JSON.stringify(telemetry));
  } catch (e) {
    console.error('Failed to save telemetry:', e);
  }
};

// Failed permission items (403) - these won't retry
const loadFailedPermissions = () => {
  try {
    const data = localStorage.getItem(FAILED_PERMISSION_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
};

const addFailedPermission = (item) => {
  const existing = loadFailedPermissions();
  existing.push({
    ...item,
    failedAt: Date.now(),
    reason: 'Insufficient permission'
  });
  localStorage.setItem(FAILED_PERMISSION_KEY, JSON.stringify(existing));
};

const clearFailedPermissions = () => {
  localStorage.removeItem(FAILED_PERMISSION_KEY);
};

// Sync state values
const SYNC_STATES = {
  ONLINE: 'online',
  OFFLINE: 'offline',
  SYNCING: 'syncing',
  REAUTH_REQUIRED: 'reauth_required',
  ERROR: 'error',
};

// Helper to get initial sync state from localStorage
const getInitialSyncState = () => {
  const reauthData = loadReauthFlag();
  const savedQueue = loadPersistedQueue();
  const telemetry = loadTelemetry();
  const failedPerms = loadFailedPermissions();
  
  if (reauthData && savedQueue.length > 0) {
    return {
      syncState: SYNC_STATES.REAUTH_REQUIRED,
      lastErrorCode: reauthData.errorCode,
      lastErrorAt: reauthData.errorAt,
      pendingCount: savedQueue.length,
      pendingWrites: savedQueue,
      telemetry,
      failedPermissions: failedPerms,
    };
  }
  
  return {
    syncState: SYNC_STATES.ONLINE,
    lastErrorCode: null,
    lastErrorAt: null,
    pendingCount: 0,
    pendingWrites: [],
    telemetry,
    failedPermissions: failedPerms,
  };
};

const SyncStatus = ({ onLoginRequired }) => {
  // Initialize state from localStorage (for reauth persistence across refresh)
  const initialState = React.useMemo(() => getInitialSyncState(), []);
  
  // State model per spec
  const [syncState, setSyncState] = useState(initialState.syncState);
  const [pendingCount, setPendingCount] = useState(initialState.pendingCount);
  const [lastSyncAt, setLastSyncAt] = useState(() => Date.now());
  const [lastErrorCode, setLastErrorCode] = useState(initialState.lastErrorCode);
  const [lastErrorAt, setLastErrorAt] = useState(initialState.lastErrorAt);
  
  // Telemetry state (P2.6)
  const [telemetry, setTelemetry] = useState(initialState.telemetry);
  const [failedPermissions, setFailedPermissions] = useState(initialState.failedPermissions);
  
  // UI state
  const [showDetails, setShowDetails] = useState(initialState.syncState === SYNC_STATES.REAUTH_REQUIRED);
  const [pendingWrites, setPendingWrites] = useState(initialState.pendingWrites);
  const [apiHealthy, setApiHealthy] = useState(true);
  
  // Update telemetry helper
  const updateTelemetry = useCallback((updates) => {
    setTelemetry(prev => {
      const newTelemetry = { ...prev, ...updates };
      saveTelemetry(newTelemetry);
      return newTelemetry;
    });
  }, []);

  // Handle sync now - only works if not in reauth_required state
  const handleSyncNow = useCallback(async () => {
    if (!navigator.onLine || syncState === SYNC_STATES.SYNCING || syncState === SYNC_STATES.REAUTH_REQUIRED) return;
    
    setSyncState(SYNC_STATES.SYNCING);
    try {
      const registration = await navigator.serviceWorker?.ready;
      registration?.active?.postMessage({ type: 'SYNC_NOW' });
    } catch (e) {
      console.error('Sync failed:', e);
      setSyncState(SYNC_STATES.ERROR);
    }
    // Syncing state will be cleared by SYNC_COMPLETE message
    setTimeout(() => {
      setSyncState(prev => prev === SYNC_STATES.SYNCING ? SYNC_STATES.ONLINE : prev);
    }, 10000); // Fallback timeout
  }, [syncState]);

  // Handle login button click - navigate to login page
  const handleLoginClick = useCallback(() => {
    if (onLoginRequired) {
      onLoginRequired();
    } else {
      // Fallback: reload page to trigger login flow
      window.location.reload();
    }
  }, [onLoginRequired]);

  useEffect(() => {
    const handleOnline = () => {
      // Track telemetry
      updateTelemetry({ lastOnlineAt: Date.now() });
      // Only set online if not in reauth_required state
      if (syncState !== SYNC_STATES.REAUTH_REQUIRED) {
        setSyncState(SYNC_STATES.ONLINE);
        setLastSyncAt(Date.now());
      }
    };
    const handleOffline = () => {
      // Track telemetry
      updateTelemetry({ lastOfflineAt: Date.now() });
      if (syncState !== SYNC_STATES.REAUTH_REQUIRED) {
        setSyncState(SYNC_STATES.OFFLINE);
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Service worker messages
    const handleMessage = (event) => {
      const { type, lastSync, pending, queue, writes, online, apiHealthy: healthy, needsReauth, errors, flushed, failed } = event.data || {};
      
      switch (type) {
        case 'SYNC_STATUS':
          // Only update if not in reauth_required state (that state is sticky until login)
          if (syncState !== SYNC_STATES.REAUTH_REQUIRED) {
            setApiHealthy(healthy !== undefined ? healthy : true);
            if (!navigator.onLine) {
              setSyncState(SYNC_STATES.OFFLINE);
            } else if (!healthy) {
              setSyncState(SYNC_STATES.ERROR);
            } else {
              setSyncState(SYNC_STATES.ONLINE);
            }
            if (lastSync) setLastSyncAt(lastSync);
          }
          if (typeof pending === 'number') setPendingCount(pending);
          break;
          
        case 'SYNC_COMPLETE':
          if (syncState !== SYNC_STATES.REAUTH_REQUIRED) {
            setSyncState(SYNC_STATES.ONLINE);
            setApiHealthy(event.data.apiHealthy !== undefined ? event.data.apiHealthy : true);
          }
          if (lastSync) setLastSyncAt(lastSync);
          if (typeof pending === 'number') setPendingCount(pending);
          // If sync completed and no pending items, clear reauth flag
          if (pending === 0) {
            clearReauthFlag();
          }
          break;
          
        case 'QUEUE_UPDATE':
          if (typeof pending === 'number') setPendingCount(pending);
          break;
          
        case 'QUEUE_FLUSHED':
          // Update telemetry with flush result
          {
            const flushResult = failed > 0 ? (flushed > 0 ? 'partial' : 'failed') : 'success';
            const errorInfo = errors?.length > 0 ? errors[0] : null;
            updateTelemetry({
              lastQueueFlushAt: Date.now(),
              lastQueueFlushResult: flushResult,
              lastQueueFlushErrorCode: errorInfo?.status || null,
              lastQueueFlushErrorMessage: errorInfo?.message || null,
            });
          }
          
          if (typeof pending === 'number') setPendingCount(pending);
          // If queue is now empty, clear reauth state
          if (pending === 0 && syncState === SYNC_STATES.REAUTH_REQUIRED) {
            setSyncState(SYNC_STATES.ONLINE);
            setLastErrorCode(null);
            setLastErrorAt(null);
            clearReauthFlag();
          }
          break;
          
        case 'REAUTH_REQUIRED':
          // Set reauth_required state
          setSyncState(SYNC_STATES.REAUTH_REQUIRED);
          setLastErrorCode(401);
          setLastErrorAt(Date.now());
          // Persist flag so it survives page refresh
          setReauthFlag(401, Date.now());
          if (typeof pending === 'number') setPendingCount(pending);
          // Auto-expand details to show the message
          setShowDetails(true);
          // Update telemetry
          updateTelemetry({
            lastQueueFlushAt: Date.now(),
            lastQueueFlushResult: 'failed',
            lastQueueFlushErrorCode: 401,
            lastQueueFlushErrorMessage: 'Re-authentication required',
          });
          break;
          
        case 'PERMISSION_DENIED':
          // 403 - Item failed due to insufficient permission
          // Don't retry, store in failed permissions log
          {
            const failedItem = event.data.item;
            if (failedItem) {
              addFailedPermission(failedItem);
              setFailedPermissions(prev => [...prev, {
                ...failedItem,
                failedAt: Date.now(),
                reason: 'Insufficient permission'
              }]);
            }
            // Update telemetry
            updateTelemetry({
              lastQueueFlushAt: Date.now(),
              lastQueueFlushResult: 'failed',
              lastQueueFlushErrorCode: 403,
              lastQueueFlushErrorMessage: 'Insufficient permission',
            });
          }
          if (typeof pending === 'number') setPendingCount(pending);
          break;
          
        case 'PENDING_WRITES':
          if (writes) {
            setPendingWrites(writes);
            setPendingCount(writes.length);
          }
          break;
          
        case 'PERSIST_QUEUE':
          // SW asks us to persist queue to localStorage
          if (queue) {
            persistQueue(queue);
            setPendingWrites(queue);
            setPendingCount(queue.length);
          }
          break;
          
        case 'WRITE_SUCCESS':
          console.log(`[Sync] ${type}:`, event.data);
          // Update telemetry on success
          updateTelemetry({
            lastQueueFlushAt: Date.now(),
            lastQueueFlushResult: 'success',
            lastQueueFlushErrorCode: null,
            lastQueueFlushErrorMessage: null,
          });
          break;
          
        case 'WRITE_CONFLICT':
          console.log(`[Sync] ${type}:`, event.data);
          break;
          
        default:
          break;
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleMessage);

    // Request initial status and restore queue
    navigator.serviceWorker?.ready.then((registration) => {
      registration.active?.postMessage({ type: 'GET_SYNC_STATUS' });
      registration.active?.postMessage({ type: 'GET_PENDING' });
      
      // Restore persisted queue to SW
      const savedQueue = loadPersistedQueue();
      if (savedQueue.length > 0) {
        registration.active?.postMessage({ type: 'LOAD_QUEUE', data: { queue: savedQueue } });
        setPendingCount(savedQueue.length);
      }
    });

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      navigator.serviceWorker?.removeEventListener('message', handleMessage);
    };
  }, [syncState, updateTelemetry]);

  if (!('serviceWorker' in navigator)) return null;

  // Derive status text based on syncState
  const getStatusText = () => {
    switch (syncState) {
      case SYNC_STATES.REAUTH_REQUIRED:
        return 'Re-auth required';
      case SYNC_STATES.OFFLINE:
        return 'Offline';
      case SYNC_STATES.SYNCING:
        return 'Syncing...';
      case SYNC_STATES.ERROR:
        return 'API issue';
      case SYNC_STATES.ONLINE:
      default:
        return 'Online';
    }
  };
  
  const getStatusClass = () => {
    switch (syncState) {
      case SYNC_STATES.REAUTH_REQUIRED:
        return 'reauth';
      case SYNC_STATES.OFFLINE:
        return 'offline';
      case SYNC_STATES.SYNCING:
        return 'syncing';
      case SYNC_STATES.ERROR:
        return 'degraded';
      case SYNC_STATES.ONLINE:
      default:
        return 'online';
    }
  };

  const statusText = getStatusText();
  const statusClass = getStatusClass();
  const lastUpdateText = formatLastUpdate(lastSyncAt);
  const isOnline = syncState === SYNC_STATES.ONLINE;
  const isSyncing = syncState === SYNC_STATES.SYNCING;
  const isReauthRequired = syncState === SYNC_STATES.REAUTH_REQUIRED;
  const ariaLabel = `Connection: ${statusText}. Last updated: ${lastUpdateText}. ${pendingCount > 0 ? `${pendingCount} pending changes.` : ''}${isReauthRequired ? ' Login required to sync changes.' : ''}`;

  return (
    <div 
      className={`sync-status ${statusClass}`}
      role="status"
      aria-live="polite"
      aria-atomic="true"
      aria-label={ariaLabel}
      data-testid="sync-status"
    >
      {/* Main indicator */}
      <div 
        className="sync-indicator"
        onClick={() => setShowDetails(!showDetails)}
        onKeyDown={(e) => e.key === 'Enter' && setShowDetails(!showDetails)}
        tabIndex={0}
      >
        <span className={`sync-dot ${statusClass}`} aria-hidden="true" />
        <span className="sync-label">{statusText}</span>
        <span className="sync-separator" aria-hidden="true">•</span>
        <span className="sync-timestamp">{lastUpdateText}</span>
        {pendingCount > 0 && (
          <span className="sync-pending" data-testid="pending-count">
            {pendingCount} pending
          </span>
        )}
      </div>
      
      {/* Expanded details panel */}
      {showDetails && (
        <div className="sync-details" data-testid="sync-details" role="region" aria-label="Sync details">
          {/* Re-auth required alert */}
          {isReauthRequired && (
            <div className="reauth-alert" data-testid="reauth-alert">
              <div className="reauth-icon">🔒</div>
              <div className="reauth-content">
                <p className="reauth-title">Re-auth required to sync</p>
                <p className="reauth-detail">
                  {pendingCount} pending change{pendingCount !== 1 ? 's' : ''} will sync after login.
                </p>
                {lastErrorAt && (
                  <p className="reauth-time">Failed: {formatLastUpdate(lastErrorAt)}</p>
                )}
              </div>
              <button 
                className="reauth-login-btn"
                onClick={handleLoginClick}
                data-testid="reauth-login-btn"
              >
                Log in to sync
              </button>
            </div>
          )}
          
          <div className="sync-detail-row">
            <span className="detail-label">Network:</span>
            <span className={`detail-value ${navigator.onLine ? 'text-success' : 'text-warning'}`}>
              {navigator.onLine ? '✓ Connected' : '⚠ Disconnected'}
            </span>
          </div>
          <div className="sync-detail-row">
            <span className="detail-label">API:</span>
            <span className={`detail-value ${apiHealthy && !isReauthRequired ? 'text-success' : 'text-error'}`}>
              {isReauthRequired ? '✗ Auth expired' : (apiHealthy ? '✓ Healthy' : '✗ Unavailable')}
            </span>
          </div>
          <div className="sync-detail-row">
            <span className="detail-label">Last updated:</span>
            <span className="detail-value">{lastUpdateText}</span>
          </div>
          
          {pendingCount > 0 && (
            <div className="sync-detail-row">
              <span className="detail-label">Pending:</span>
              <span className={`detail-value ${isReauthRequired ? 'text-error' : 'text-warning'}`}>
                {pendingCount} change{pendingCount !== 1 ? 's' : ''}
              </span>
            </div>
          )}
          
          {/* Sync Now button - disabled when reauth required */}
          <button 
            className="sync-now-btn"
            onClick={handleSyncNow}
            disabled={!navigator.onLine || isSyncing || isReauthRequired}
            data-testid="sync-now-btn"
          >
            {isSyncing ? '↻ Syncing...' : '↻ Sync now'}
          </button>
          
          {/* Offline note */}
          {syncState === SYNC_STATES.OFFLINE && (
            <div className="sync-offline-note">
              <p>You&apos;re viewing cached data.</p>
              <p className="sync-edit-warning">⚠ Edits are disabled while offline.</p>
            </div>
          )}
          
          {/* Pending changes will sync message */}
          {isReauthRequired && pendingCount > 0 && (
            <div className="sync-pending-note">
              <p>Pending changes will sync after login.</p>
            </div>
          )}
          
          {/* Show pending writes summary */}
          {pendingCount > 0 && pendingWrites.length > 0 && (
            <div className="pending-summary">
              <p className="pending-title">Pending changes:</p>
              <ul className="pending-list">
                {pendingWrites.slice(0, 3).map((w, i) => (
                  <li key={w.client_request_id || i}>
                    {w.action || 'Update'} - queued {formatLastUpdate(w.queuedAt)}
                  </li>
                ))}
                {pendingWrites.length > 3 && (
                  <li className="pending-more">+{pendingWrites.length - 3} more</li>
                )}
              </ul>
            </div>
          )}
          
          {/* 403 Failed permissions alert */}
          {failedPermissions.length > 0 && (
            <div className="permission-alert" data-testid="permission-alert">
              <p className="permission-title">⚠ {failedPermissions.length} action{failedPermissions.length !== 1 ? 's' : ''} failed (permission)</p>
              <p className="permission-detail">Insufficient permission. Contact an admin/supervisor.</p>
              <ul className="permission-list">
                {failedPermissions.slice(0, 2).map((item, i) => (
                  <li key={item.client_request_id || i}>
                    {item.action || 'Update'} - {formatLastUpdate(item.failedAt)}
                  </li>
                ))}
              </ul>
              <button 
                className="permission-dismiss-btn"
                onClick={() => {
                  clearFailedPermissions();
                  setFailedPermissions([]);
                }}
                data-testid="dismiss-failed-permissions"
              >
                Dismiss
              </button>
            </div>
          )}
          
          {/* Telemetry section (P2.6) - shown only in expanded view */}
          {(telemetry.lastQueueFlushAt || telemetry.lastOnlineAt || telemetry.lastOfflineAt) && (
            <details className="telemetry-section" data-testid="telemetry-section">
              <summary className="telemetry-summary">Sync telemetry</summary>
              <div className="telemetry-content">
                {telemetry.lastQueueFlushAt && (
                  <div className="telemetry-row">
                    <span className="telemetry-label">Last flush:</span>
                    <span className="telemetry-value">{formatLastUpdate(telemetry.lastQueueFlushAt)}</span>
                  </div>
                )}
                {telemetry.lastQueueFlushResult && (
                  <div className="telemetry-row">
                    <span className="telemetry-label">Result:</span>
                    <span className={`telemetry-value ${telemetry.lastQueueFlushResult === 'success' ? 'text-success' : 'text-error'}`}>
                      {telemetry.lastQueueFlushResult}
                    </span>
                  </div>
                )}
                {telemetry.lastQueueFlushErrorCode && (
                  <div className="telemetry-row">
                    <span className="telemetry-label">Error:</span>
                    <span className="telemetry-value text-error">
                      {telemetry.lastQueueFlushErrorCode} - {telemetry.lastQueueFlushErrorMessage || 'Unknown'}
                    </span>
                  </div>
                )}
                {telemetry.lastOnlineAt && (
                  <div className="telemetry-row">
                    <span className="telemetry-label">Last online:</span>
                    <span className="telemetry-value">{formatLastUpdate(telemetry.lastOnlineAt)}</span>
                  </div>
                )}
                {telemetry.lastOfflineAt && (
                  <div className="telemetry-row">
                    <span className="telemetry-label">Last offline:</span>
                    <span className="telemetry-value">{formatLastUpdate(telemetry.lastOfflineAt)}</span>
                  </div>
                )}
              </div>
            </details>
          )}
        </div>
      )}

      <style>{`
        .sync-status {
          position: fixed;
          bottom: 1rem;
          left: 1rem;
          z-index: 1000;
        }
        
        .sync-indicator {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 0.75rem;
          background: var(--bg-secondary, #f8fafc);
          border: 1px solid var(--border, #e2e8f0);
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 500;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .sync-indicator:hover {
          box-shadow: 0 4px 12px rgba(0,0,0,0.12);
        }
        
        .sync-indicator:focus-visible {
          outline: 2px solid var(--primary, #3b82f6);
          outline-offset: 2px;
        }
        
        .sync-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          flex-shrink: 0;
        }
        
        .sync-dot.online {
          background: #10b981;
          animation: pulse 2s infinite;
        }
        
        .sync-dot.offline {
          background: #f59e0b;
        }
        
        .sync-label {
          font-weight: 600;
        }
        
        .sync-status.online .sync-label { color: #10b981; }
        .sync-status.offline .sync-label { color: #f59e0b; }
        .sync-status.degraded .sync-label { color: #ef4444; }
        .sync-status.reauth .sync-label { color: #ef4444; }
        .sync-status.syncing .sync-label { color: #3b82f6; }
        
        .sync-dot.degraded {
          background: #ef4444;
          animation: none;
        }
        
        .sync-dot.reauth {
          background: #ef4444;
          animation: pulse-error 1s infinite;
        }
        
        .sync-dot.syncing {
          background: #3b82f6;
          animation: pulse 1s infinite;
        }
        
        @keyframes pulse-error {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.2); }
        }
        
        .text-error { color: #ef4444; }
        
        .sync-separator {
          color: var(--text-muted, #94a3b8);
          font-size: 0.625rem;
        }
        
        .sync-timestamp {
          color: var(--text-muted, #94a3b8);
          font-weight: 400;
        }
        
        .sync-pending {
          background: #fef3c7;
          color: #d97706;
          padding: 0.125rem 0.5rem;
          border-radius: 10px;
          font-size: 0.6875rem;
          font-weight: 600;
          margin-left: 0.25rem;
        }
        
        .sync-details {
          position: absolute;
          bottom: 100%;
          left: 0;
          margin-bottom: 0.5rem;
          padding: 0.75rem;
          background: var(--bg-primary, #ffffff);
          border: 1px solid var(--border, #e2e8f0);
          border-radius: 8px;
          box-shadow: 0 4px 16px rgba(0,0,0,0.12);
          min-width: 240px;
          font-size: 0.8125rem;
        }
        
        .sync-detail-row {
          display: flex;
          justify-content: space-between;
          gap: 1rem;
          margin-bottom: 0.375rem;
        }
        
        .detail-label { color: var(--text-muted, #94a3b8); }
        .detail-value { font-weight: 500; }
        .text-success { color: #10b981; }
        .text-warning { color: #f59e0b; }
        
        .sync-now-btn {
          width: 100%;
          margin-top: 0.5rem;
          padding: 0.5rem;
          background: var(--primary, #3b82f6);
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s ease;
        }
        
        .sync-now-btn:hover:not(:disabled) {
          background: var(--primary-dark, #2563eb);
        }
        
        .sync-now-btn:disabled {
          background: var(--text-muted, #94a3b8);
          cursor: not-allowed;
        }
        
        .sync-offline-note {
          margin-top: 0.5rem;
          padding-top: 0.5rem;
          border-top: 1px solid var(--border-light, #f1f5f9);
          font-size: 0.75rem;
          color: var(--text-muted, #94a3b8);
        }
        
        .sync-offline-note p { margin: 0 0 0.25rem 0; }
        .sync-edit-warning { color: #f59e0b; font-weight: 500; }
        
        .sync-pending-note {
          margin-top: 0.5rem;
          padding: 0.5rem;
          background: #fef2f2;
          border-radius: 4px;
          font-size: 0.75rem;
          color: #991b1b;
        }
        
        .sync-pending-note p { margin: 0; }
        
        /* Re-auth alert styles */
        .reauth-alert {
          margin-bottom: 0.75rem;
          padding: 0.75rem;
          background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
          border: 1px solid #fecaca;
          border-radius: 8px;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        
        .reauth-icon {
          font-size: 1.5rem;
          text-align: center;
        }
        
        .reauth-content {
          text-align: center;
        }
        
        .reauth-title {
          margin: 0 0 0.25rem 0;
          font-weight: 600;
          color: #991b1b;
          font-size: 0.875rem;
        }
        
        .reauth-detail {
          margin: 0;
          color: #b91c1c;
          font-size: 0.75rem;
        }
        
        .reauth-time {
          margin: 0.25rem 0 0 0;
          color: #dc2626;
          font-size: 0.6875rem;
          font-style: italic;
        }
        
        .reauth-login-btn {
          width: 100%;
          padding: 0.625rem 1rem;
          background: #dc2626;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 0.8125rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s ease;
        }
        
        .reauth-login-btn:hover {
          background: #b91c1c;
        }
        
        .reauth-login-btn:focus-visible {
          outline: 2px solid #ef4444;
          outline-offset: 2px;
        }
        
        .pending-summary {
          margin-top: 0.5rem;
          padding-top: 0.5rem;
          border-top: 1px solid var(--border-light, #f1f5f9);
          font-size: 0.75rem;
        }
        
        .pending-title {
          margin: 0 0 0.25rem 0;
          color: var(--text-muted, #94a3b8);
        }
        
        .pending-list {
          margin: 0;
          padding-left: 1rem;
          color: var(--text-secondary, #64748b);
        }
        
        .pending-list li { margin-bottom: 0.125rem; }
        .pending-more { color: var(--text-muted, #94a3b8); font-style: italic; }
        
        /* 403 Permission alert styles */
        .permission-alert {
          margin-top: 0.75rem;
          padding: 0.75rem;
          background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
          border: 1px solid #fcd34d;
          border-radius: 8px;
          font-size: 0.75rem;
        }
        
        .permission-title {
          margin: 0 0 0.25rem 0;
          font-weight: 600;
          color: #92400e;
        }
        
        .permission-detail {
          margin: 0 0 0.5rem 0;
          color: #b45309;
        }
        
        .permission-list {
          margin: 0 0 0.5rem 0;
          padding-left: 1rem;
          color: #92400e;
          font-size: 0.6875rem;
        }
        
        .permission-dismiss-btn {
          width: 100%;
          padding: 0.375rem;
          background: transparent;
          color: #92400e;
          border: 1px solid #d97706;
          border-radius: 4px;
          font-size: 0.6875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s ease;
        }
        
        .permission-dismiss-btn:hover {
          background: #fef3c7;
        }
        
        /* Telemetry section styles (P2.6) */
        .telemetry-section {
          margin-top: 0.75rem;
          padding-top: 0.5rem;
          border-top: 1px solid var(--border-light, #f1f5f9);
          font-size: 0.6875rem;
        }
        
        .telemetry-summary {
          cursor: pointer;
          color: var(--text-muted, #94a3b8);
          font-weight: 500;
          padding: 0.25rem 0;
          user-select: none;
        }
        
        .telemetry-summary:hover {
          color: var(--text-secondary, #64748b);
        }
        
        .telemetry-content {
          margin-top: 0.5rem;
          padding: 0.5rem;
          background: var(--bg-tertiary, #f8fafc);
          border-radius: 4px;
        }
        
        .telemetry-row {
          display: flex;
          justify-content: space-between;
          gap: 0.5rem;
          margin-bottom: 0.25rem;
        }
        
        .telemetry-row:last-child {
          margin-bottom: 0;
        }
        
        .telemetry-label {
          color: var(--text-muted, #94a3b8);
        }
        
        .telemetry-value {
          font-family: monospace;
          font-size: 0.625rem;
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
};

export default SyncStatus;
